// Copyright 2026 Zibby Lab. All rights reserved.
//
// UpdateAgentModal — the "Update <Agent>" confirm dialog opened from the
// "Update available" pill on PipelineDetailPage. Shows installed → current
// version, the release notes, and a Cancel / Update action pair.
//
// Action pairing mirrors the Trigger modal exactly: the primary action is the
// shared PillButton (the header "Trigger" pill, with its built-in `loading`
// spinner overlay — no "…" suffix, no width jump), and Cancel is a subtle
// text link, not a bordered button.

import React from 'react';
import PropTypes from 'prop-types';
import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import PillButton from '../../components/shared/PillButton';
// The modal shell styles (pipeline-update-*) are shared with AppDetailPage's
// apps-update modal and live in PipelineDetailPage.css; import it directly so
// this component doesn't depend on the parent page having loaded it.
import './PipelineDetailPage.css';
import './UpdateAgentModal.css';

export default function UpdateAgentModal({
  title,
  marketplace,
  updating,
  onClose,
  onUpdate,
}) {
  return (
    <div
      className="pipeline-update-overlay"
      onClick={() => !updating && onClose()}
      role="presentation"
    >
      <div
        className="pipeline-update-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pipeline-update-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pipeline-update-modal-head">
          <h2 id="pipeline-update-title" className="pipeline-update-modal-title">
            Update {title}
          </h2>
          <button
            type="button"
            className="pipeline-update-modal-close"
            aria-label="Close"
            onClick={() => !updating && onClose()}
            disabled={updating}
          >
            <IonIcon icon={closeOutline} />
          </button>
        </div>
        <div className="pipeline-update-modal-body">
          <p className="pipeline-update-version-line">
            <span className="pipeline-update-version-from">
              v{marketplace.installedVersion || '?'}
            </span>
            <span className="pipeline-update-version-arrow">→</span>
            <span className="pipeline-update-version-to">
              v{marketplace.currentVersion || '?'}
            </span>
          </p>
          {marketplace.releaseNotes ? (
            <div className="pipeline-update-notes">
              {/* Render as plain text for v1; markdown rendering
                  is a follow-up. Whitespace is preserved so
                  authors' bullet lists still read OK. */}
              <pre className="pipeline-update-notes-pre">
                {marketplace.releaseNotes}
              </pre>
            </div>
          ) : (
            <p className="pipeline-update-no-notes">
              No release notes provided by the author.
            </p>
          )}
          <p className="pipeline-update-preserved">
            Your environment variables, schedule, and custom
            agent name will be preserved.
          </p>
        </div>
        <div className="pipeline-update-modal-actions update-agent-modal-actions">
          <button
            type="button"
            className="pipeline-update-cancel-link"
            onClick={onClose}
            disabled={updating}
          >
            Cancel
          </button>
          <PillButton onClick={onUpdate} loading={updating}>
            Update
          </PillButton>
        </div>
      </div>
    </div>
  );
}

UpdateAgentModal.propTypes = {
  title: PropTypes.string,
  marketplace: PropTypes.shape({
    installedVersion: PropTypes.string,
    currentVersion: PropTypes.string,
    releaseNotes: PropTypes.string,
  }).isRequired,
  updating: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
